"use client";

import { ArrowRight, Check } from "lucide-react";
import { Link } from "@/components/common/Link";
import { AddToCartButton, EcommerceProductsProvider, useEcommerceProducts } from "@/integrations/wordpress/WordPressEcommerceProductsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function FeaturedSolutionsConsumer() {
  const {
    products,
    loading,
    error,
    refetch
  } = useEcommerceProducts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-16">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="space-y-6">
            <div className="aspect-[4/5] bg-muted/40 animate-pulse rounded-lg" />
            <div className="h-px bg-border" />
            <div className="h-6 w-3/4 bg-muted/40 animate-pulse" />
            <div className="h-3 w-1/2 bg-muted/40 animate-pulse" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-sm text-sage-deep font-default uppercase tracking-widest">
        Unable to load solutions.{" "}
        <button onClick={refetch} className="underline hover:text-primary transition-colors">
          Retry
        </button>
      </div>;
  }
  if (products.length === 0) {
    return <p className="text-sage-deep font-default uppercase tracking-widest text-sm">
        No featured solutions available.
      </p>;
  }
  return <div className="space-y-12">
      <h2 className="sr-only">The Gentle Threshold Ritual: B.Y.E. Essentials</h2>
      <div className="grid grid-cols-1 gap-12 md:grid-cols-2 lg:grid-cols-3 lg:gap-16">
        {products.map((product, i) => {
        const isLimited = i === 0;
        const benefits = [{
          id: 1,
          text: "Pediatrician Approved"
        }, {
          id: 2,
          text: "Non-Toxic Formula"
        }, {
          id: 3,
          text: "Travel-Ready Size"
        }];
        return <article data-index={i} key={product.id} className="group relative border border-border hover:border-primary transition-colors duration-500 rounded-lg shadow-md bg-white">
              {isLimited && <div className="absolute top-4 left-4 z-10 font-default text-[10px] uppercase tracking-[0.2em] text-white bg-primary/90 backdrop-blur-sm px-3 py-1 rounded-lg">
                  New Arrival
                </div>}

              <Link to={product.link} className="block">
                <div className="relative aspect-[4/5] overflow-hidden rounded-t-lg bg-secondary">
                  {product.featured_image_url && <img src={product.featured_image_url} alt={`${product.title} in a soft nursery setting, newborn-safe formulation`} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-[800ms] ease-out group-hover:scale-[1.04]" />}
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-10 transition-opacity duration-[800ms] pointer-events-none bg-gradient-to-tr from-transparent via-transparent to-primary" />
                </div>
              </Link>

              <div className="h-px w-full bg-border" />

              <div className="px-6 pt-6 pb-6 space-y-4">
                <div>
                  <Link to={product.link}>
                    <h3 className="font-default text-2xl text-sage-deep leading-tight hover:text-primary transition-colors duration-300">
                      {product.title}
                    </h3>
                  </Link>
                  
                  <ul className="mt-3 space-y-1.5">
                    {benefits.map(benefit => <li key={benefit.id} className="flex items-center gap-2 text-[11px] text-sage-deep/60 uppercase tracking-wider font-medium">
                        <Check className="size-3 text-primary" />
                        {benefit.text}
                      </li>)}
                  </ul>

                  <Link to={`/use-case/${product.slug}`} className="text-[10px] uppercase tracking-[0.15em] text-primary font-semibold hover:opacity-80 transition-opacity block mt-4">
                    View Use Case Guide
                  </Link>
                </div>

                {product.short_description ? <div className="font-default text-sm text-sage-deep/70 line-clamp-2 [&_p]:mb-0 [&_p:last-child]:mb-0" dangerouslySetInnerHTML={{
              __html: product.short_description
            }} /> : <p className="font-default text-sm text-sage-deep/70">
                    A clinically-inspired, family-first mist engineered for uncompromising safety and absolute peace of mind during every transition.
                  </p>}

                <div className="flex items-center justify-between pt-4 border-t border-border/50">
                  <span className="font-default text-lg text-sage-deep font-medium">
                    ${product.price?.toFixed?.(2) ?? product.price}
                  </span>
                  <AddToCartButton productId={product.id} variant="ghost" size="sm" text={`Shop ${product.title}`} showQuantity={false} className="w-auto" buttonClassName="bg-primary text-white font-default text-sm px-5 py-2.5 rounded-lg hover:brightness-90 transition-all" />
                </div>
              </div>
            </article>;
      })}
      </div>
    </div>;
}
export default function FeaturedSolutions() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section data-section-id="5116" id="featured-bye-solutions" className="bg-cream-warm text-sage-deep py-24 md:py-40 px-6 md:px-10 lg:px-16">
      <div className="max-w-7xl mx-auto">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 mb-20 md:mb-28">
          <div className="lg:col-span-7 space-y-8">
            <p className="font-default text-xs uppercase tracking-[0.3em] text-sage-deep/60">
              B.Y.E. Essentials
            </p>
            <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.1] text-sage-deep">
              A <span className="italic font-light">sanctuary</span>
              <br />
              for your family.
            </h2>
          </div>

          <div className="lg:col-span-5 flex flex-col justify-end space-y-8">
            <p className="font-default text-lg md:text-xl text-sage-deep/80 leading-relaxed max-w-md">
              Your family's safety is our sacred mission. We've crafted these gentle hygiene mists to serve as a trusted, invisible shield—preserving the purity of your home and the peace of your heart.
            </p>
            <Link to="/ShopPage" className="group inline-flex items-center gap-3 font-default text-sm uppercase tracking-[0.2em] text-sage-deep hover:text-primary transition-colors duration-500 w-fit">
              <span className="border-b border-sage-deep/20 group-hover:border-primary pb-1 transition-colors duration-500">
                Explore All
              </span>
              <ArrowRight className="w-4 h-4 transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </div>
        </div>

        <EcommerceProductsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <FeaturedSolutionsConsumer />
        </EcommerceProductsProvider>
      </div>
    </section>;
}